import React, { useEffect, useState } from 'react';
import { ThemeProvider, useTheme } from '../../../contexts/ThemeContext';
import { getMessageById } from '../../../repositories/agentService';
import ChatMessage from '../../../models/chat_message';
import PDFPageContainer from '../../utils/PDFPageContainer';
import LoadingSpinner from '../../common/LoadingSpinner';
import ErrorWidget from '../../common/ErrorWidget';
import SchoolHeader from '../../common/SchoolHeader';
import { parseMessageContent } from '../../../utils/chat_utils';
import { AiResponseContentType } from "../../../models/chat_content";
import { renderCSVTable, renderArrayTable } from "../formatters/TableFormatters";
import { renderGraph } from "../formatters/GraphFormatter";
import DiaryFormatter from "../formatters/DiaryFormatter";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import remarkGfm from "remark-gfm";
import '../../utils/PDFStyles.css';
import * as chatUtils from '../../../utils/chat_utils';
import useExportStore from '../../../store/exportStore';
import { MessageExportSourceType } from '../../../constants/constants';

const MessagePDFExportContent = () => {
    const theme = useTheme();
    const { colors, baseColors } = theme;
    const [message, setMessage] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [profilePicUrl, setProfilePicUrl] = useState(null);
    const [profilePicLoaded, setProfilePicLoaded] = useState(false);

    const [iconUrl, setIconUrl] = useState(null);

    const [schoolHeading, setSchoolHeading] = useState(null);
    const [schoolSubHeading, setSchoolSubHeading] = useState(null);
    const [schoolAddress, setSchoolAddress] = useState(null);
    const [type, setType] = useState(null);
    const [showButton, setShowButton] = useState(false);
    const { downloadPDF, loading: isExporting } = useExportStore();
    
    useEffect(() => {
        const fetchMessage = async () => {
            try {
                const params = new URLSearchParams(window.location.search);
                const schoolId = params.get('schoolId');
                const messageId = params.get('messageId')??params.get('itemId');
                const secretKey = params.get('apiSecret');
                // const userToken = params.get('userToken');
                const type = params.get('type');
                setType(type);
                const reportId = params.get('reportId');
                // Get raw encoded values for Firebase Storage URLs
                // Firebase Storage URLs contain encoded slashes (%2F) in the path that must be preserved
                // URLSearchParams.get() automatically decodes these, breaking the Firebase URL structure
                const searchString = window.location.search;
                const getRawParam = (paramName) => {
                    const regex = new RegExp(`[?&]${paramName}=([^&]*)`);
                    const match = searchString.match(regex);
                    return match ? match[1] : null;
                };
                
                const encodedProfilePic = getRawParam('profilePic');
                const encodedIconUrl = getRawParam('iconUrl');
                
                // Get decoded values for school data
                const _schoolHeading = params.get('schoolHeading');
                const _schoolSubHeading = params.get('schoolSubHeading');
                const _schoolAddress = params.get('schoolAddress');
                ///if the school data is present decode it from the url
                if(_schoolHeading){
                    setSchoolHeading(_schoolHeading);
                }
                if(_schoolSubHeading){
                    setSchoolSubHeading(_schoolSubHeading);
                }
                if(_schoolAddress){
                    setSchoolAddress(_schoolAddress);
                }
     
                if (!schoolId || !messageId) {
                    throw new Error("Missing required query parameters: schoolId, messageId");
                }

                // Set profile picture URL if provided
                if (encodedProfilePic) {
                    console.log(`decodedProfilePic: ${decodeURIComponent(encodedProfilePic)}`);
                    setProfilePicUrl(decodeURIComponent(encodedProfilePic));
                }
                if (encodedIconUrl) {
                    setIconUrl(decodeURIComponent(encodedIconUrl));
                }

                setLoading(true);
                const result = await getMessageById({ schoolId, messageId, type, reportId, secretKey });

                if (result.success) {
                    const chatMessage = ChatMessage.fromJson(result.value);
                    setMessage(chatMessage);
                } else {
                    setError(result.message || 'Failed to fetch message');
                }
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchMessage();

        const handleScroll = () => {
            if (window.scrollY > 50) {
                setShowButton(true);
            } else {
                setShowButton(false);
            }
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const renderMarkdown = (text) => (
        <div className="markdown-content page-item">
            <ReactMarkdown
                rehypePlugins={[rehypeRaw]}
                remarkPlugins={[remarkGfm]}
                components={{
                    table: ({ children, ...props }) => (
                        <div className="overflow-x-auto my-4">
                            <table
                                className="min-w-full border-collapse rounded-lg overflow-hidden shadow-sm"
                                style={{ borderColor: colors.cardBorder }}
                                {...props}
                            >
                                {children}
                            </table>
                        </div>
                    ),
                    th: ({ children, ...props }) => (
                        <th
                            className="px-4 py-3 text-left font-semibold text-sm border-b-2 border-r"
                            style={{ 
                                backgroundColor: colors.tableHeaderBg, 
                                color: colors.tableHeaderText,
                                borderColor: colors.cardBorder,
                             }}
                            {...props}
                        >
                            {children}
                        </th>
                    ),
                    td: ({ children, ...props }) => (
                        <td
                            className="px-4 py-3 text-sm border-b border-r"
                            style={{ 
                                backgroundColor: colors.tableCellBg, 
                                color: colors.tableCellText,
                                borderColor: colors.cardBorder
                            }}
                            {...props}
                        >
                            {children}
                        </td>
                    ),
                }}
            >
                {chatUtils.replaceEscapedNewlines(text)}
            </ReactMarkdown>
        </div>
    );

    const renderFormattedContent = (segment) => {
        switch (segment.type) {
            case AiResponseContentType.CSV:
            case AiResponseContentType.TABLE:
                return (
                    <div className="space-y-4 page-item">
                        {segment.title && <h3 className="text-lg font-semibold" style={{ color: colors.text }}>{segment.title}</h3>}
                        {segment.type === AiResponseContentType.CSV ? renderCSVTable(segment.data, theme) : renderArrayTable(segment.data, theme)}
                    </div>
                );
            case AiResponseContentType.GRAPH:
                return <div className="page-item">{renderGraph({ ...segment.data, title: segment.title }, theme, false)}</div>;
            case AiResponseContentType.LINK:
                return (
                    <div className="page-item">
                        <a href={segment.data} target="_blank" rel="noopener noreferrer" className="underline hover:opacity-80 transition-opacity" style={{ color: baseColors?.primary || '#e08a08' }}>
                            {segment.title || segment.data}
                        </a>
                    </div>
                );
            case AiResponseContentType.DIARY:
                return <div className="page-item"><DiaryFormatter content={segment.data} /></div>;
            case AiResponseContentType.MARKDOWN:
                return renderMarkdown(segment.data);
            default:
                if (typeof segment.data === 'string') {
                    return renderMarkdown(segment.data);
                }
                return null;
        }
    };

    const handleProfilePicLoad = () => {
        console.log('Profile picture loaded successfully');
        setProfilePicLoaded(true);
    };

    const handleProfilePicError = () => {
        console.error('Error loading profile picture');
        setProfilePicLoaded(false);
    };

    const renderProfilePicture = () => {
        if (!profilePicUrl) return null;

        return (
            <div className="relative">
                {/* Default placeholder */}
                <div 
                    className={`w-32 h-32 rounded-full bg-gray-300 flex items-center justify-center ${profilePicLoaded ? 'hidden' : 'block'}`}
                    style={{ backgroundColor: colors.cardBorder }}
                >
                    <svg className="w-12 h-12 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                    </svg>
                </div>
                
                {/* Actual profile picture */}
                <img
                    src={profilePicUrl}
                    alt="Profile"
                    className={`w-32 h-32 rounded-full object-cover ${profilePicLoaded ? 'block' : 'hidden'}`}
                    onLoad={handleProfilePicLoad}
                    onError={handleProfilePicError}
                />
            </div>
        );
    };

    const renderContent = () => {
        if (!message || !message.content) return null;
        
        const parsed = parseMessageContent(message.content);

        if (parsed.type === AiResponseContentType.MULTI) {
            return (
                <>
                    {parsed.segments.map((segment, index) => (
                        <React.Fragment key={index}>
                            {renderFormattedContent(segment)}
                        </React.Fragment>
                    ))}
                </>
            );
        }
        return renderFormattedContent(parsed);
    };


    if (loading) return <div className="flex justify-center items-center min-h-screen"><LoadingSpinner /></div>;
    if (error) return <div className="p-8"><ErrorWidget error={error} /></div>;
    if (!message) return <div className="p-8 text-center text-gray-500">No message data available.</div>;

    return (
        <div className="min-h-screen bg-gray-50">
            <PDFPageContainer className="bg-white shadow-md" enableAnimation={false}>
                {/* Header Container with Profile Picture and School Header */}
                <div className="page-item flex items-center py-4 px-6">
                    {/* Profile Picture - Left side with margin */}
                    <div className="flex-shrink-0 mr-6">
                        {renderProfilePicture()}
                    </div>
                    
                    {/* School Header - Positioned based on profile picture presence */}
                    <div className={`${profilePicUrl ? 'flex-1' : 'flex-1 flex justify-center'}`}>
                        <SchoolHeader 
                            schoolHeading={schoolHeading} 
                            schoolSubHeading={schoolSubHeading} 
                            schoolAddress={schoolAddress}
                            iconUrl={iconUrl}
                            className={profilePicUrl ? 'text-left' : 'text-center'}
                        />
                    </div>
                </div>
                
                {/* Message Content */}
                {renderContent()}
            </PDFPageContainer>

            {/* Floating Download Button */}
            {type === MessageExportSourceType.STUDENT_REPORT && (
                <button
                    onClick={() => downloadPDF({ url: window.location.href }, 'Student_Report')}
                    disabled={isExporting}
                    className={`fixed bottom-8 right-8 px-6 py-3 rounded-full shadow-2xl font-bold text-white transition-all duration-500 transform flex items-center gap-2 z-50 ${
                        showButton ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10 pointer-events-none'
                    } hover:scale-105 active:scale-95`}
                    style={{ 
                        backgroundColor: baseColors?.primary || '#e08a08',
                        opacity: isExporting ? 0.7 : showButton ? 1 : 0,
                        cursor: isExporting ? 'not-allowed' : 'pointer'
                    }}
                >
                    {isExporting ? (
                        <>
                            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                            Exporting...
                        </>
                    ) : (
                        <>
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                            </svg>
                            Download PDF
                        </>
                    )}
                </button>
            )}
        </div>
    );
};

const MessagePDFExportWindow = () => (
    <ThemeProvider forceTheme="light">
        <MessagePDFExportContent />
    </ThemeProvider>
);

export default MessagePDFExportWindow;